(function() {
    const FREE_GIFT_SELECTOR = '.c-cart-free-gift';
    const ADD_BUTTON_SELECTOR = '.c-cfg-add-button';
    const CART_CONTAINER_SELECTOR = '#CartContainer';
    const SUBTOTAL_SELECTOR = '.ajaxcart__subtotal, .c-cart-subtotal';
    let updateTimeout = null;
    let cartObserver = null;
    let drawerObserver = null;
    let subtotalObserver = null;
    let lastSubtotalValue = null;
    let giftRemovalInProgress = false;
    let enforcementIntervalStarted = false;
    let observerDebounceTimer = null;
    let isUserInteracting = false;
    let userInteractionTimeout = null;
    let cartChangeInProgress = false;
    let checkoutButtonDisabled = false;
    const freeGiftLog = (...args) => console.log('[freeGiftMonitor]', ...args);
  
    function formatCurrency(value, currencyCode) {
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: currencyCode,
          maximumFractionDigits: 0
        }).format(value);
      } catch (error) {
        return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
      }
    }
  
    // Instant currency conversion - runs immediately without async delays
    function convertThresholdInstantly() {
      const thresholdDisplayEl = document.querySelector('.c-cfg-threshold-amount');
      if (!thresholdDisplayEl) {
        return;
      }
  
      const container = document.querySelector(FREE_GIFT_SELECTOR);
      if (!container) {
        return;
      }
      
      const thresholdUSD = parseFloat(container.dataset.freeGiftThreshold || '0') || 175;
      let currentCurrency = 'USD';
      let thresholdInCurrentCurrency = thresholdUSD;
      
      // Get currency immediately from Shopify currency object (no API call needed)
      if (window.Shopify && window.Shopify.currency) {
        if (window.Shopify.currency.active) {
          currentCurrency = window.Shopify.currency.active;
        }
        
        // Convert threshold from USD to current currency
        if (currentCurrency !== 'USD') {
          if (window.Shopify.currency.rates && window.Shopify.currency.rates[currentCurrency] && window.Shopify.currency.rates['USD']) {
            // Convert using exchange rates
            thresholdInCurrentCurrency = thresholdUSD * (window.Shopify.currency.rates[currentCurrency] / window.Shopify.currency.rates['USD']);
          } else if (window.Shopify.currency.rate) {
            // Use currency multiplier if available
            thresholdInCurrentCurrency = thresholdUSD * window.Shopify.currency.rate;
          }
        }
      }
      
      // Update the display immediately - check if value needs conversion
      if (thresholdInCurrentCurrency > 0) {
        const currentText = thresholdDisplayEl.textContent.trim();
        const convertedText = formatCurrency(thresholdInCurrentCurrency, currentCurrency);
        
        // Only update if the current text is different (not already converted)
        // This prevents infinite loops with MutationObserver
        if (currentText !== convertedText) {
          thresholdDisplayEl.textContent = convertedText;
        }
      }
    }
  
    function convertThresholdForCart(thresholdUSD, cart) {
      if (!thresholdUSD) {
        return 0;
      }
      if (!cart || !cart.currency || cart.currency === 'USD') {
        return thresholdUSD;
      }
  
      if (window.Shopify && window.Shopify.currency) {
        const currencyInfo = window.Shopify.currency;
        const target = cart.currency;
        if (currencyInfo.rates && currencyInfo.rates[target] && currencyInfo.rates['USD']) {
          return thresholdUSD * (currencyInfo.rates[target] / currencyInfo.rates['USD']);
        }
        if (currencyInfo.rate && currencyInfo.active === target) {
          return thresholdUSD * currencyInfo.rate;
        }
      }
  
      return thresholdUSD;
    }
  
    async function fetchCartSnapshot() {
      try {
        freeGiftLog('fetching /cart.js snapshot');
        const response = await fetch('/cart.js');
        return await response.json();
      } catch (error) {
        console.warn('[freeGiftMonitor] failed to fetch cart snapshot', error);
        return null;
      }
    }
  
    function getFreeGiftElements() {
      const container = document.querySelector(FREE_GIFT_SELECTOR);
      if (!container) {
        return null;
      }
      const threshold = parseFloat(container.dataset.freeGiftThreshold || '0');
      const productId = container.dataset.freeGiftProductId || '';
      const addButton = container.querySelector(ADD_BUTTON_SELECTOR);
      return { container, threshold, productId, addButton };
    }
  
    // Validate subtotal before allowing add action
    async function validateSubtotalBeforeAdd() {
      const freeGiftElements = getFreeGiftElements();
      if (!freeGiftElements) {
        return false;
      }

      const thresholdUSD = freeGiftElements.threshold || 175;
      let subtotalValue = 0;
      let currentCurrency = 'USD';
      let thresholdInCurrentCurrency = thresholdUSD;

      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        
        if (cart) {
          currentCurrency = cart.currency || 'USD';
          
          if (cart.items_subtotal_price) {
            subtotalValue = cart.items_subtotal_price / 100;
          }
          
          // Convert threshold from USD to current currency
          if (currentCurrency !== 'USD') {
            if (window.Shopify && window.Shopify.currency) {
              if (window.Shopify.currency.rates && window.Shopify.currency.rates[currentCurrency] && window.Shopify.currency.rates['USD']) {
                thresholdInCurrentCurrency = thresholdUSD * (window.Shopify.currency.rates[currentCurrency] / window.Shopify.currency.rates['USD']);
              } else if (window.Shopify.currency.active && window.Shopify.currency.active === currentCurrency) {
                const currencyMultiplier = window.Shopify.currency.rate || 1;
                thresholdInCurrentCurrency = thresholdUSD * currencyMultiplier;
              }
            }
          }

          // Check if gift is already in cart
          const freeGiftProductId = parseInt(freeGiftElements.productId, 10);
          if (!Number.isNaN(freeGiftProductId) && Array.isArray(cart.items)) {
            const hasGift = cart.items.some(item => item.product_id === freeGiftProductId);
            if (hasGift) {
              return false; // Gift already in cart
            }
          }
        }
      } catch (error) {
        console.warn('[freeGiftMonitor] Failed to validate subtotal', error);
        return false;
      }
      
      // Fallback to DOM parsing
      if (subtotalValue === 0) {
        subtotalValue = getSubtotalFromDOM();
      }
      
      // Return true only if subtotal is above threshold
      return subtotalValue >= thresholdInCurrentCurrency;
    }

    let checkoutReenableTimeout = null;
    
    // Toggle checkout button - disable during cart changes
    function toggleCheckoutButton(disabled) {
      const checkoutButtons = document.querySelectorAll('.c-cart-checkout, .cart__checkout, button[name="checkout"], .cart--button-checkout');
      
      checkoutButtons.forEach(button => {
        if (disabled) {
          checkoutButtonDisabled = true;
          button.disabled = true;
          button.setAttribute('aria-disabled', 'true');
          button.classList.add('disabled');
          button.style.pointerEvents = 'none';
          button.style.opacity = '0.5';
          button.style.cursor = 'not-allowed';
          
          // Prevent form submission
          button.onclick = function(e) {
            e.preventDefault();
            e.stopPropagation();
            return false;
          };
          
          // Schedule fallback re-enable in case cart:updated doesn't fire
          if (checkoutReenableTimeout) {
            clearTimeout(checkoutReenableTimeout);
          }
          checkoutReenableTimeout = setTimeout(() => {
            if (cartChangeInProgress) {
              cartChangeInProgress = false;
              toggleCheckoutButton(false);
            }
            checkoutReenableTimeout = null;
          }, 3000); // 3 second fallback timeout
        } else {
          // Clear fallback timeout if re-enabling
          if (checkoutReenableTimeout) {
            clearTimeout(checkoutReenableTimeout);
            checkoutReenableTimeout = null;
          }
          
          checkoutButtonDisabled = false;
          button.disabled = false;
          button.removeAttribute('aria-disabled');
          button.classList.remove('disabled');
          button.style.pointerEvents = '';
          button.style.opacity = '';
          button.style.cursor = '';
          
          // Restore click handler
          button.onclick = null;
        }
      });
    }

    function toggleAddButton(addButton, disabled) {
      if (!addButton) {
        return;
      }
      
      addButton.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      
      if (disabled) {
        // For anchor tags, we can't use disabled attribute, so use class and pointer-events
        addButton.classList.add('disabled');
        addButton.style.pointerEvents = 'none';
        addButton.style.opacity = '0.5';
        addButton.style.cursor = 'not-allowed';
        // Prevent click
        addButton.onclick = function(e) {
          e.preventDefault();
          return false;
        };
      } else {
        // Enable the button
        addButton.classList.remove('disabled');
        addButton.style.pointerEvents = '';
        addButton.style.opacity = '';
        addButton.style.cursor = '';
        // Restore click handler
        addButton.onclick = null;
      }
    }
  
    function getSubtotalFromDOM() {
      const subtotalElement = document.querySelector('.c-cart-subtotal.ajaxcart__subtotal');
      if (!subtotalElement) {
        return 0;
      }
      
      // Find the money value - it could be in a <p> tag or <span> with class money
      const moneyElement = subtotalElement.querySelector('p, span.money, .money');
      if (!moneyElement) {
        // Try getting text directly from subtotal element
        const directText = subtotalElement.textContent.trim();
        if (directText) {
          const cleaned = directText.replace(/[^0-9.]/g, '');
          const value = parseFloat(cleaned);
          if (!isNaN(value) && value > 0) {
            return value;
          }
        }
        return 0;
      }
      
      // Get text content and parse the money value
      const moneyText = moneyElement.textContent.trim();
      
      // Remove currency symbols, commas, and parse number
      // First remove commas, then remove everything except digits and decimal point
      let cleaned = moneyText.replace(/,/g, '').replace(/[^0-9.]/g, '');
      const moneyValue = parseFloat(cleaned);
      
      if (isNaN(moneyValue) || moneyValue === 0) {
        return 0;
      }
      
      return moneyValue;
    }
  
    function getStateFromDataset(container) {
      const subtotalValue = parseInt(container.dataset.cartSubtotalValue || '0', 10);
      const hasGift = container.dataset.cartHasFreeGift === 'true';
      const threshold = parseFloat(container.dataset.freeGiftThreshold || '0') || 0;
  
      return {
        subtotalValue: Number.isNaN(subtotalValue) ? 0 : subtotalValue,
        hasGift,
        threshold,
      };
    }
  
    function removeStaleGiftRow() {
      const staleRow = document.querySelector('.c-cart-line-item-free-gift, .c-cart-line-item[data-free-gift="true"]');
      if (staleRow) {
        staleRow.remove();
        if (window.theme && typeof window.theme.fetchCart === 'function') {
          window.theme.fetchCart();
        }
      }
    }
  
    async function removeGiftFromCart(productId, cartData = null) {
      if (!productId) {
        freeGiftLog('removeGiftFromCart aborted: missing productId');
        return;
      }
  
      let cart = cartData;
  
      try {
        if (!cart || !Array.isArray(cart.items)) {
          const response = await fetch('/cart.js');
          cart = await response.json();
        }
      } catch (error) {
        return;
      }
  
      if (!cart || !Array.isArray(cart.items)) {
        freeGiftLog('removeGiftFromCart aborted: cart.items missing');
        return;
      }
  
      const giftItem = cart.items.find(item => item.product_id === productId);
      if (!giftItem) {
        freeGiftLog('removeGiftFromCart: gift already removed');
        return;
      }
  
      if (giftRemovalInProgress) {
        freeGiftLog('removeGiftFromCart skipped: already in progress');
        return;
      }
  
      const giftIdentifier = giftItem.key || giftItem.id || giftItem.variant_id;
      if (!giftIdentifier) {
        freeGiftLog('removeGiftFromCart aborted: missing identifier');
        return;
      }
  
      giftRemovalInProgress = true;
  
      try {
        freeGiftLog('removeGiftFromCart sending /cart/change.js', { productId, giftIdentifier });
        await fetch('/cart/change.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            id: giftIdentifier,
            quantity: 0
          })
        });
  
        freeGiftLog('removeGiftFromCart request sent, refreshing cart');
        removeStaleGiftRow();
        if (window.theme && typeof window.theme.fetchCart === 'function') {
          window.theme.fetchCart();
        } else if (window.Vue) {
          const app = document.querySelector('#CartContainer');
          if (app && app.__vue__ && typeof app.__vue__.fetchCart === 'function') {
            app.__vue__.fetchCart();
          }
        }
  
        setTimeout(updateFreeGiftState, 200);
      } catch (error) {
        console.error('[freeGiftMonitor] removeGiftFromCart failed', error);
      } finally {
        giftRemovalInProgress = false;
        freeGiftLog('removeGiftFromCart complete');
      }
    }
  
    async function updateFreeGiftState() {
      const freeGiftElements = getFreeGiftElements();
      if (!freeGiftElements) {
        return;
      }
  
      // Get threshold from data attribute (stored as USD dollars)
      const thresholdUSD = freeGiftElements.threshold || 175;
      const freeGiftProductId = parseInt(freeGiftElements.productId, 10);
      
      // Fetch cart to get subtotal and currency
      let subtotalValue = 0;
      let currentCurrency = 'USD';
      let thresholdInCurrentCurrency = thresholdUSD;
      let cartData = null;
      let hasGiftFromCart = null;
      
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        cartData = cart;
        
        if (cart) {
          currentCurrency = cart.currency || 'USD';
          
          // Get subtotal in current currency (items_subtotal_price is in cents)
          if (cart.items_subtotal_price) {
            subtotalValue = cart.items_subtotal_price / 100;
            lastSubtotalValue = subtotalValue; // Track last known subtotal
          }
          
          // Convert threshold from USD to current currency
          if (currentCurrency !== 'USD') {
            // Try to get exchange rate from Shopify currency object
            if (window.Shopify && window.Shopify.currency) {
              if (window.Shopify.currency.rates && window.Shopify.currency.rates[currentCurrency] && window.Shopify.currency.rates['USD']) {
                // Convert using exchange rates
                thresholdInCurrentCurrency = thresholdUSD * (window.Shopify.currency.rates[currentCurrency] / window.Shopify.currency.rates['USD']);
              } else if (window.Shopify.currency.active && window.Shopify.currency.active === currentCurrency) {
                // Try alternative method: use currency multiplier if available
                const currencyMultiplier = window.Shopify.currency.rate || 1;
                thresholdInCurrentCurrency = thresholdUSD * currencyMultiplier;
              }
            }
          }
  
          if (Array.isArray(cart.items)) {
            if (!Number.isNaN(freeGiftProductId)) {
              hasGiftFromCart = cart.items.some(item => item.product_id === freeGiftProductId);
              freeGiftElements.container.dataset.cartHasFreeGift = hasGiftFromCart ? 'true' : 'false';
            }
          }
          if (typeof cart.items_subtotal_price === 'number') {
            freeGiftElements.container.dataset.cartSubtotalValue = String(cart.items_subtotal_price);
          }
  
          // Update visible threshold amount in the drawer
          const thresholdDisplayEl = freeGiftElements.container.querySelector('.c-cfg-threshold-amount');
          if (thresholdDisplayEl && thresholdInCurrentCurrency > 0) {
            thresholdDisplayEl.textContent = formatCurrency(thresholdInCurrentCurrency, currentCurrency);
          }
        }
      } catch (error) {
      }
      
      // Fallback to DOM parsing if cart API didn't work
      if (subtotalValue === 0) {
        subtotalValue = getSubtotalFromDOM();
        if (subtotalValue > 0) {
          lastSubtotalValue = subtotalValue; // Track last known subtotal
        }
      }
      
      // Check if gift is in cart
      const datasetState = getStateFromDataset(freeGiftElements.container);
      const hasGift = hasGiftFromCart !== null ? hasGiftFromCart : datasetState.hasGift;
      
      // Compare subtotal (in current currency) with converted threshold
      const belowThreshold = subtotalValue < thresholdInCurrentCurrency;
      
      // Keep option hidden any time the gift is already in the cart
      const shouldHide = hasGift;
      // Disable add button only when gift is not in cart and subtotal is below the threshold
      const shouldDisable = !hasGift && belowThreshold;
      // Gift is in cart but subtotal dropped below threshold - need to remove it
      const shouldRemoveGift = hasGift && belowThreshold;

      freeGiftElements.container.classList.toggle('c-cart-free-gift-hidden', shouldHide);
      
      // If gift is in cart but subtotal dropped below threshold, remove it
      if (shouldRemoveGift && !Number.isNaN(freeGiftProductId)) {
        await removeGiftFromCart(freeGiftProductId, cartData);
      } else if (!hasGift) {
        removeStaleGiftRow();
      }
      
      if (freeGiftElements.addButton) {
        // Disable button if below threshold (only when gift isn't already in cart)
        toggleAddButton(freeGiftElements.addButton, shouldDisable);
      }
    }
  
    function getCurrencyConversionToUSD(value, currentCurrency) {
      // value: amount in current currency (not cents)
      if (!value || currentCurrency === 'USD') {
        return value || 0;
      }
  
      if (window.Shopify && window.Shopify.currency) {
        const currencyInfo = window.Shopify.currency;
        if (currencyInfo.rates && currencyInfo.rates[currentCurrency] && currencyInfo.rates['USD']) {
          const rate = currencyInfo.rates['USD'] / currencyInfo.rates[currentCurrency];
          return value * rate;
        }
  
        if (currencyInfo.rate) {
          const multiplier = parseFloat(currencyInfo.rate);
          if (!Number.isNaN(multiplier) && multiplier !== 0) {
            // Shopify.currency.rate converts from shop currency to current currency
            // So to convert back to USD, divide by multiplier
            return value / multiplier;
          }
        }
      }
  
      return value;
    }
  
    function applyImmediateFreeGiftState() {
      const freeGiftElements = getFreeGiftElements();
      if (!freeGiftElements) {
        return;
      }

      const datasetState = getStateFromDataset(freeGiftElements.container);
      const subtotalCents = datasetState.subtotalValue || 0;
      const hasGift = datasetState.hasGift;
      const thresholdUSD = freeGiftElements.threshold || 175;
      const subtotalValue = subtotalCents / 100;

      let currentCurrency = 'USD';
      if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
        currentCurrency = window.Shopify.currency.active;
      }

      const subtotalInUSD = getCurrencyConversionToUSD(subtotalValue, currentCurrency);

      const belowThreshold = subtotalInUSD < thresholdUSD;
      const shouldHide = hasGift;
      const shouldDisable = !hasGift && belowThreshold;

      freeGiftElements.container.classList.toggle('c-cart-free-gift-hidden', shouldHide);

      if (freeGiftElements.addButton) {
        toggleAddButton(freeGiftElements.addButton, shouldDisable);
      }
    }
  
    let immediateUpdateFrame = null;
    let needsImmediateConversion = false;
    let needsImmediateButtonUpdate = false;
    let pendingAsyncUpdate = null;
  
    function requestImmediateUpdate({ convert = true, updateButtons = true } = {}) {
      if (convert) {
        needsImmediateConversion = true;
      }
      if (updateButtons) {
        needsImmediateButtonUpdate = true;
      }
      if (immediateUpdateFrame) {
        return;
      }
      immediateUpdateFrame = requestAnimationFrame(() => {
        immediateUpdateFrame = null;
        if (needsImmediateConversion) {
          convertThresholdInstantly();
          needsImmediateConversion = false;
        }
        if (needsImmediateButtonUpdate) {
          applyImmediateFreeGiftState();
          needsImmediateButtonUpdate = false;
        }
      });
    }
  
    function requestAsyncUpdate(delay = 200) {
      if (pendingAsyncUpdate) {
        return;
      }
      pendingAsyncUpdate = setTimeout(() => {
        pendingAsyncUpdate = null;
        updateFreeGiftState();
      }, delay);
    }
  
    function normalizeScheduleOptions(input) {
      if (!input || typeof input !== 'object') {
        return {};
      }
      if (Array.isArray(input)) {
        return {};
      }
      if ('immediateFetch' in input) {
        return input;
      }
      return {};
    }
  
    function scheduleUpdate(optionsOrEvent) {
      // Skip if user is actively interacting with cart (quantity changes, remove buttons)
      if (isUserInteracting) {
        return;
      }
      
      const options = normalizeScheduleOptions(optionsOrEvent);
      requestImmediateUpdate({ convert: true, updateButtons: true });
      attachCartObserver();
      if (options.immediateFetch) {
        updateFreeGiftState();
        return;
      }
      requestAsyncUpdate();
    }
  
    // Debounced version of scheduleUpdate to prevent excessive calls
    function scheduleUpdateDebounced(optionsOrEvent) {
      // Clear existing debounce timer
      if (observerDebounceTimer) {
        clearTimeout(observerDebounceTimer);
      }
      
      // Skip if user is actively interacting
      if (isUserInteracting) {
        return;
      }
      
      // Debounce observer callbacks to prevent performance issues
      observerDebounceTimer = setTimeout(() => {
        observerDebounceTimer = null;
        scheduleUpdate(optionsOrEvent);
      }, 300); // 300ms debounce
    }
  
    function ensureFreeGiftWatcher() {
      if (document.querySelector(FREE_GIFT_SELECTOR)) {
        // Convert threshold instantly when element is found
        requestImmediateUpdate({ convert: true, updateButtons: true });
        attachCartObserver();
        attachSubtotalObserver();
        scheduleUpdate({ immediateFetch: true });
        if (drawerObserver) {
          drawerObserver.disconnect();
          drawerObserver = null;
        }
        return;
      }
  
      if (drawerObserver) {
        return;
      }
  
      // Use a more efficient approach - check periodically instead of watching entire body
      let checkCount = 0;
      const maxChecks = 20; // Check for 10 seconds (20 * 500ms)
      
      const checkInterval = setInterval(() => {
        checkCount++;
        if (document.querySelector(FREE_GIFT_SELECTOR)) {
          requestImmediateUpdate({ convert: true, updateButtons: true });
          attachCartObserver();
          attachSubtotalObserver();
          scheduleUpdate({ immediateFetch: true });
          clearInterval(checkInterval);
          drawerObserver = null;
        } else if (checkCount >= maxChecks) {
          clearInterval(checkInterval);
          drawerObserver = null;
        }
      }, 500);
      
      drawerObserver = { disconnect: () => clearInterval(checkInterval) };
    }
  
    function attachCartObserver() {
      // Don't attach if already attached or if user is interacting
      if (cartObserver || isUserInteracting) {
        return false;
      }

      const cartContainer = document.querySelector(CART_CONTAINER_SELECTOR);
      if (!cartContainer) {
        return false;
      }

      // Only watch for major structural changes, not every DOM mutation
      cartObserver = new MutationObserver((mutations) => {
        // Disable checkout when cart structure changes
        const hasStructuralChange = mutations.some(mutation => {
          if (mutation.type === 'childList' && 
              (mutation.addedNodes.length > 0 || mutation.removedNodes.length > 0)) {
            // Cart structure is changing, disable checkout
            if (!cartChangeInProgress) {
              cartChangeInProgress = true;
              toggleCheckoutButton(true);
            }
            return true;
          }
          return false;
        });
        
        // Completely skip if user is interacting
        if (isUserInteracting) {
          return;
        }
        
        // Only process if there are actual structural changes (items added/removed)
        if (!hasStructuralChange) {
          // Check for other significant changes
          const hasSignificantChange = mutations.some(mutation => {
            // Only care about childList changes, ignore attribute changes
            if (mutation.type !== 'childList') {
              return false;
            }
            // Only care if nodes were actually added or removed
            if (mutation.addedNodes.length === 0 && mutation.removedNodes.length === 0) {
              return false;
            }
            // Ignore if it's just a quantity input change
            const target = mutation.target;
            if (target && (
              target.closest && target.closest('.c-cart-line-item-quantity') ||
              target.classList && target.classList.contains('c-cart-line-item-quantity')
            )) {
              return false;
            }
            return true;
          });
          
          if (!hasSignificantChange) {
            return;
          }
        }
        
        // Heavy debounce for cart changes
        scheduleUpdateDebounced();
      });
      
      // Only watch direct children, not entire subtree - much more efficient
      cartObserver.observe(cartContainer, { 
        childList: true, 
        subtree: false, // Changed to false - only watch direct children
        attributes: false 
      });
      
      return true;
    }
  
    async function checkAndRemoveGiftIfNeeded() {
      const freeGiftElements = getFreeGiftElements();
      if (!freeGiftElements) {
        return;
      }
  
      const productId = parseInt(freeGiftElements.productId, 10);
      if (Number.isNaN(productId)) {
        return;
      }
  
      const thresholdUSD = freeGiftElements.threshold || 175;
      let subtotalValue = 0;
      let currentCurrency = 'USD';
      let thresholdInCurrentCurrency = thresholdUSD;
  
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        
        if (cart) {
          currentCurrency = cart.currency || 'USD';
          
          // Get subtotal in current currency (items_subtotal_price is in cents)
          if (cart.items_subtotal_price) {
            subtotalValue = cart.items_subtotal_price / 100;
          }
          
          // Convert threshold from USD to current currency
          if (currentCurrency !== 'USD') {
            if (window.Shopify && window.Shopify.currency) {
              if (window.Shopify.currency.rates && window.Shopify.currency.rates[currentCurrency] && window.Shopify.currency.rates['USD']) {
                thresholdInCurrentCurrency = thresholdUSD * (window.Shopify.currency.rates[currentCurrency] / window.Shopify.currency.rates['USD']);
              } else if (window.Shopify.currency.active && window.Shopify.currency.active === currentCurrency) {
                const currencyMultiplier = window.Shopify.currency.rate || 1;
                thresholdInCurrentCurrency = thresholdUSD * currencyMultiplier;
              }
            }
          }
  
          const cartItems = Array.isArray(cart.items) ? cart.items : [];
          // Check if gift is in cart
          const hasGift = cartItems.some(item => item.product_id === productId);
          const belowThreshold = subtotalValue < thresholdInCurrentCurrency;
  
          // If gift is in cart and subtotal is below threshold, remove it
          if (hasGift && belowThreshold) {
            await removeGiftFromCart(productId, cart);
          }
      }
    } catch (error) {
    }
  }
  
    async function enforceGiftConsistency(reason = 'manual') {
      const freeGiftElements = getFreeGiftElements();
      if (!freeGiftElements) {
        freeGiftLog('enforce skipped: widget missing', { reason });
        return;
      }
  
      const productId = parseInt(freeGiftElements.productId, 10);
      if (Number.isNaN(productId)) {
        freeGiftLog('enforce skipped: invalid product id', { reason });
        return;
      }
  
      const cart = await fetchCartSnapshot();
      if (!cart || !Array.isArray(cart.items)) {
        freeGiftLog('enforce skipped: invalid cart snapshot', { reason });
        return;
      }
  
      const subtotalValue = cart.items_subtotal_price ? cart.items_subtotal_price / 100 : 0;
      const thresholdConverted = convertThresholdForCart(freeGiftElements.threshold || 175, cart);
      const hasGift = cart.items.some(item => item.product_id === productId);
      const belowThreshold = subtotalValue < thresholdConverted;
  
      freeGiftLog('enforce check', {
        reason,
        subtotalValue,
        thresholdConverted,
        hasGift
      });
  
      freeGiftElements.container.dataset.cartHasFreeGift = hasGift ? 'true' : 'false';
      freeGiftElements.container.dataset.cartSubtotalValue = String(cart.items_subtotal_price || 0);
      applyImmediateFreeGiftState();
  
      if (!belowThreshold) {
        if (!hasGift) {
          freeGiftLog('cart above threshold but gift missing', { reason });
        } else {
          freeGiftLog('cart above threshold with gift present', { reason });
        }
        return;
      }

      if (hasGift) {
        freeGiftLog('cart below threshold, removing gift', { reason });
        await removeGiftFromCart(productId, cart);
      } else {
        freeGiftLog('cart below threshold and gift already absent', { reason });
        removeStaleGiftRow();
      }
    }
  
    let subtotalDebounceTimer = null;
    
    function attachSubtotalObserver() {
      // Don't attach if already attached or user is interacting
      if (subtotalObserver || isUserInteracting) {
        return;
      }
  
      const subtotalElement = document.querySelector(SUBTOTAL_SELECTOR);
      if (!subtotalElement) {
        return;
      }
  
      subtotalObserver = new MutationObserver(async (mutations) => {
        // Completely skip if user is interacting
        if (isUserInteracting) {
          return;
        }
        
        // Heavy debounce for subtotal changes
        if (subtotalDebounceTimer) {
          clearTimeout(subtotalDebounceTimer);
        }
        
        subtotalDebounceTimer = setTimeout(async () => {
          subtotalDebounceTimer = null;
          if (isUserInteracting) return; // Double check
          
          const currentSubtotal = getSubtotalFromDOM();
          
          // Only proceed if subtotal value actually changed
          if (currentSubtotal !== lastSubtotalValue && currentSubtotal > 0) {
            lastSubtotalValue = currentSubtotal;
            await checkAndRemoveGiftIfNeeded();
          }
        }, 800); // Increased debounce to 800ms
      });
  
      // Only watch the subtotal element itself, not subtree
      subtotalObserver.observe(subtotalElement, {
        childList: true,
        subtree: false, // Changed to false - only watch direct children
        characterData: true
      });
    }
  
    // Function to mark user interaction and temporarily disable observers
    function markUserInteraction() {
      isUserInteracting = true;
      cartChangeInProgress = true;
      
      // Disable checkout button when cart change starts
      toggleCheckoutButton(true);
      
      // Disconnect observers during interaction
      if (cartObserver) {
        cartObserver.disconnect();
        cartObserver = null;
      }
      if (subtotalObserver) {
        subtotalObserver.disconnect();
        subtotalObserver = null;
      }
      
      // Clear any existing timeout
      if (userInteractionTimeout) {
        clearTimeout(userInteractionTimeout);
      }
      
      // Re-enable observers after user interaction completes
      userInteractionTimeout = setTimeout(() => {
        isUserInteracting = false;
        userInteractionTimeout = null;
        // Re-attach observers and schedule update
        attachCartObserver();
        attachSubtotalObserver();
        scheduleUpdate({ immediateFetch: false });
      }, 1500); // 1.5 seconds after last interaction
    }
  
    document.addEventListener('DOMContentLoaded', () => {
      if (!document.body) {
        return;
      }

      // Initial setup
      requestImmediateUpdate();
      setTimeout(() => requestImmediateUpdate(), 200); // Single retry for currency

      ensureFreeGiftWatcher();
      enforceGiftConsistency('dom-ready');
      
      if (!enforcementIntervalStarted) {
        enforcementIntervalStarted = true;
        setInterval(() => enforceGiftConsistency('interval'), 10000); // Increased to 10s
      }
  
      // Single consolidated event listener for user interactions
      document.addEventListener('click', (event) => {
        const target = event.target;
        
        // Detect quantity selector or remove button clicks
        if (target.closest('.c-cart-line-item-quantity, .grid__item.c-cart-line-item-quantity') ||
            target.closest('[data-cart-remove], .cart__remove') ||
            target.matches('input[type="number"]') ||
            target.closest('input[type="number"]')) {
          // Disable checkout when cart change starts
          cartChangeInProgress = true;
          toggleCheckoutButton(true);
          markUserInteraction();
          return;
        }
        
        // Detect add to cart button clicks
        if (target.closest('.c-product--add-to-cart-button, .product--add-to-cart-button, .c-rp-add-button')) {
          // Disable checkout when adding to cart
          cartChangeInProgress = true;
          toggleCheckoutButton(true);
        }
        
        // Handle drawer title click
        if (target.closest('.drawer__title')) {
          requestImmediateUpdate();
          requestAsyncUpdate();
          return;
        }
        
        // Handle cart drawer or add to cart button
        if (target.closest('.drawer--cart, .site-header__cart-toggle, .c-product--add-to-cart-button, .product--add-to-cart-button')) {
          requestImmediateUpdate();
          scheduleUpdate();
          setTimeout(() => {
            attachSubtotalObserver();
            checkAndRemoveGiftIfNeeded();
          }, 500);
          return;
        }
      }, { passive: true });
  
      // Single consolidated input/change listener
      document.addEventListener('input', (event) => {
        if (event.target.matches('input[type="number"]') && 
            event.target.closest('.c-cart-line-item-quantity, .grid__item.c-cart-line-item-quantity')) {
          // Disable checkout when quantity input changes
          cartChangeInProgress = true;
          toggleCheckoutButton(true);
          markUserInteraction();
        }
      }, { passive: true });
      
      document.addEventListener('change', (event) => {
        if (event.target.matches('input[type="number"]') && 
            event.target.closest('.c-cart-line-item-quantity, .grid__item.c-cart-line-item-quantity')) {
          // Disable checkout when quantity changes
          cartChangeInProgress = true;
          toggleCheckoutButton(true);
          markUserInteraction();
        }
      }, { passive: true });
  
      // Cart updated event - re-enable checkout when cart is ready
      document.addEventListener('cart:updated', () => {
        // Cart update is complete, re-enable checkout after a short delay
        setTimeout(() => {
          cartChangeInProgress = false;
          toggleCheckoutButton(false);
        }, 300); // Small delay to ensure cart is fully updated
        
        if (!isUserInteracting) {
          requestImmediateUpdate();
          scheduleUpdate({ immediateFetch: true });
          setTimeout(() => checkAndRemoveGiftIfNeeded(), 500);
          enforceGiftConsistency('cart:updated');
        }
      });
      
  
      // Single lightweight observer for threshold element (only when it exists)
      function setupThresholdWatcher() {
        const thresholdEl = document.querySelector('.c-cfg-threshold-amount');
        if (thresholdEl && !thresholdEl.dataset.conversionWatcher) {
          thresholdEl.dataset.conversionWatcher = 'true';
          
          const thresholdObserver = new MutationObserver(() => {
            if (!isUserInteracting) {
              requestImmediateUpdate({ convert: true, updateButtons: false });
            }
          });
          
          thresholdObserver.observe(thresholdEl, {
            childList: true,
            subtree: false, // Only direct children
            characterData: true
          });
        }
      }
  
      // Check for threshold element periodically (lightweight)
      const checkThreshold = setInterval(() => {
        if (document.querySelector('.c-cfg-threshold-amount')) {
          setupThresholdWatcher();
          clearInterval(checkThreshold);
        }
      }, 500);
      
      // Cleanup after 10 seconds
      setTimeout(() => clearInterval(checkThreshold), 10000);
      
      // Initial check
      setupThresholdWatcher();
    });
  })();



// Free Gift Product Restrictions - Prevent quantity changes and PDP access
(function() {
    let freeGiftProductId = null;
    
    // Get free gift product ID
    function getFreeGiftProductId() {
      if (freeGiftProductId) return freeGiftProductId;
      
      // First check global variable (set in theme.liquid)
      if (window.freeGiftProductId) {
        freeGiftProductId = parseInt(window.freeGiftProductId, 10);
        return freeGiftProductId;
      }
      
      // Then check cart container
      const container = document.querySelector('.c-cart-free-gift');
      if (container && container.dataset.freeGiftProductId) {
        freeGiftProductId = parseInt(container.dataset.freeGiftProductId, 10);
        return freeGiftProductId;
      }
      
      // Finally check hidden element
      const hiddenEl = document.querySelector('.c-cfg-product-id');
      if (hiddenEl) {
        freeGiftProductId = parseInt(hiddenEl.textContent.trim(), 10);
        return freeGiftProductId;
      }
      
      return null;
    }
  
    // Block PDP access - runs immediately
    function blockFreeGiftPDP() {
      const productId = getFreeGiftProductId();
      if (!productId) return;
  
      const pathMatch = window.location.pathname.match(/\/products\/([^\/\?]+)/);
      if (!pathMatch) return;
  
      const productHandle = pathMatch[1];
      
      // Check product JSON data
      const productJsonEl = document.querySelector('script[type="application/json"][data-product-json], script[type="application/json"]#ProductJson');
      if (productJsonEl) {
        try {
          const productData = JSON.parse(productJsonEl.textContent);
          if (productData && productData.id === productId) {
            console.warn('[Free Gift] Blocked: Direct access to free gift product page');
            window.location.replace('/');
            return;
          }
        } catch (e) {}
      }
  
      // Check data attributes
      if (document.querySelector('[data-product-id="' + productId + '"]')) {
        const productForm = document.querySelector('form[action*="/cart/add"]');
        if (productForm) {
          console.warn('[Free Gift] Blocked: Direct access to free gift product page');
          window.location.replace('/');
          return;
        }
      }
  
      // Fetch and check
      fetch('/products/' + productHandle + '.js')
        .then(response => response.ok ? response.json() : null)
        .then(product => {
          if (product && product.id === productId) {
            console.warn('[Free Gift] Blocked: Direct access to free gift product page');
            window.location.replace('/');
          }
        })
        .catch(() => {});
    }
  
    // Block links to free gift
    function blockFreeGiftLinks() {
      const productId = getFreeGiftProductId();
      if (!productId) return;
  
      document.addEventListener('click', function(event) {
        const link = event.target.closest('a[href*="/products/"]');
        if (!link) return;
  
        const href = link.getAttribute('href');
        const pathMatch = href.match(/\/products\/([^\/\?]+)/);
        if (!pathMatch) return;
  
        const linkProductId = link.getAttribute('data-product-id');
        if (linkProductId && parseInt(linkProductId, 10) === productId) {
          event.preventDefault();
          event.stopPropagation();
          console.warn('[Free Gift] Blocked: Link to free gift product');
          return false;
        }
  
        const handle = pathMatch[1];
        fetch('/products/' + handle + '.js')
          .then(response => response.ok ? response.json() : null)
          .then(product => {
            if (product && product.id === productId) {
              event.preventDefault();
              event.stopPropagation();
              console.warn('[Free Gift] Blocked: Link to free gift product');
              window.location.href = '/';
            }
          })
          .catch(() => {});
      }, true);
    }
  
    // Prevent quantity changes
    function preventFreeGiftQuantityChanges() {
      const productId = getFreeGiftProductId();
      if (!productId) return;
  
      const cartContainer = document.querySelector('#CartContainer');
      if (cartContainer && cartContainer.__vue__) {
        const vueInstance = cartContainer.__vue__;
        if (vueInstance.setQuantity) {
          const originalSetQuantity = vueInstance.setQuantity;
          vueInstance.setQuantity = function(line, quantity) {
            if (this.cart && this.cart.items && this.cart.items[line - 1]) {
              const item = this.cart.items[line - 1];
              if (item.product_id === productId) {
                console.warn('[Free Gift] Quantity changes blocked');
                return;
              }
            }
            return originalSetQuantity.call(this, line, quantity);
          };
        }
      }
  
      document.addEventListener('change', function(event) {
        if (event.target.matches('.ajaxcart__qty-num, input[name="updates[]"]')) {
          const row = event.target.closest('.ajaxcart__row, .c-cart-line-item');
          if (row && row.classList.contains('c-cart-line-item-free-gift')) {
            event.preventDefault();
            event.target.value = 1;
            console.warn('[Free Gift] Quantity changes blocked');
            return false;
          }
        }
      }, true);
  
      document.addEventListener('click', function(event) {
        const button = event.target.closest('.ajaxcart__qty-adjust, .ajaxcart__qty--minus, .ajaxcart__qty--plus');
        if (button) {
          const row = button.closest('.ajaxcart__row, .c-cart-line-item');
          if (row && row.classList.contains('c-cart-line-item-free-gift')) {
            event.preventDefault();
            event.stopPropagation();
            console.warn('[Free Gift] Quantity changes blocked');
            return false;
          }
        }
      }, true);
    }
  
    // Run immediately
    blockFreeGiftPDP();
    blockFreeGiftLinks();
  
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function() {
        blockFreeGiftPDP();
        setTimeout(() => preventFreeGiftQuantityChanges(), 1000);
      });
    } else {
      setTimeout(() => preventFreeGiftQuantityChanges(), 1000);
    }
  
    document.addEventListener('cart:updated', function() {
      setTimeout(() => preventFreeGiftQuantityChanges(), 500);
    });
  })();